import React, { FC, useContext, useState } from 'react';
import { useFormikContext } from 'formik';
import { useHasAccess } from '@broadleaf/admin-components/dist/authentication/hooks';
import Button from '@broadleaf/admin-components/dist/common/elements/Button';
import { getEndpointByType } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import { IMetadataAction } from '@broadleaf/admin-components/dist/types/metadata';
import useTracking from '@src/components/view/navigation/hooks/useTracking';
import { ContextParameters } from '@broadleaf/admin-components/dist/tracking/utils/ContextRequest';
import { ToastContainerContext } from '@broadleaf/admin-components/dist/common/index';

interface PricingActivity {
  type: string;
  activityId: string;
  activityTypeName: string;
}

interface DownloadPricingActivityActionProps {
  actionDefinition: IMetadataAction;
  disabled?: boolean;
  row: PricingActivity;
}

const DownloadPricingActivityAction: FC<DownloadPricingActivityActionProps> = props => {
  const { actionDefinition, disabled, row } = props;
  const tracking = useTracking();
  const { values } = useFormikContext<{ id: string }>();
  const hasAccess = useHasAccess(
    actionDefinition.scope,
    actionDefinition.operationType
  );
  const toast = useContext(ToastContainerContext);

  const [loading, setLoading] = useState(false);

  if (
    !hasAccess ||
    (row.type !== 'EXPORT' && row.type !== 'IMPORT') ||
    !row.activityId
  ) {
    return null;
  }

  const download = async () => {
    setLoading(true);
    try {
      const contextParams = {
        [ContextParameters.TRACKING]: tracking,
        id: values.id,
        [row.type === 'IMPORT' ? 'importId' : 'exportId']: row.activityId
      };
      const response = await request<Blob>(
        {
          ...getEndpointByType(actionDefinition, 'DOWNLOAD'),
          responseType: 'blob'
        },
        contextParams
      );
      if (response.status === 204) {
        toast.addToast('Activity is not ready', {
          autoClose: 5000,
          closeOnClick: true,
          type: 'error'
        });
        return;
      }
      const { data, headers } = response;
      const filename =
        /filename="?([^";]+)"?/i.exec(
          headers['content-disposition'] || ''
        )?.[1] ||
        (row.type === 'IMPORT'
          ? 'pricing-import.xlsx'
          : `pricing-export-${row.activityTypeName}.xlsx`);
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      try {
        document.body.appendChild(link);
        link.click();
      } finally {
        link.remove();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.addToast('Unable to download the activity. Please try again.', {
        autoClose: 5000,
        closeOnClick: true,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button disabled={disabled || loading} onClick={download} type="button">
      {actionDefinition.label}
    </Button>
  );
};

export default DownloadPricingActivityAction;
